<template>
  <div class="box-border flex overflow-hidden flex-col p-2 h-screen">
    <div
      class="flex overflow-hidden flex-col flex-1 min-h-0 bg-white rounded-lg border border-gray-200 shadow-sm"
    >
      <!-- Header -->
      <div class="px-2 py-2 border-b border-gray-200">
        <h3 class="text-lg font-semibold text-gray-900 capitalize">
          Voucher List
        </h3>
      </div>

      <!-- Content -->
      <div class="flex overflow-hidden flex-col flex-1 p-2 min-h-0">
        <!-- Static Filters Section -->
        <div
          class="flex-shrink-0 px-4 py-2 mb-1 bg-gray-50 rounded-lg border border-gray-200"
        >
          <div
            class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-7"
          >
            <!-- Date Range -->
            <div class="col-span-2 space-y-2">
              <label class="block text-xs font-medium text-gray-600"
                >Date Range</label
              >
              <div class="flex space-x-2">
                <input
                  v-model="filters.fromDate"
                  type="date"
                  class="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <input
                  v-model="filters.toDate"
                  type="date"
                  class="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div class="flex flex-wrap gap-1">
                <button
                  type="button"
                  @click="setDateFilter('currentFY')"
                  :class="dateQuickFilterClass('currentFY')"
                >
                  Current FY
                </button>
                <button
                  type="button"
                  @click="setDateFilter('prevFY')"
                  :class="dateQuickFilterClass('prevFY')"
                >
                  Prev FY
                </button>
                <button
                  type="button"
                  @click="setDateFilter('today')"
                  :class="dateQuickFilterClass('today')"
                >
                  Today
                </button>
                <button
                  type="button"
                  @click="setDateFilter('yesterday')"
                  :class="dateQuickFilterClass('yesterday')"
                >
                  Yesterday
                </button>
                <button
                  type="button"
                  @click="setDateFilter('thisWeek')"
                  :class="dateQuickFilterClass('thisWeek')"
                >
                  This Week
                </button>
                <button
                  type="button"
                  @click="setDateFilter('thisMonth')"
                  :class="dateQuickFilterClass('thisMonth')"
                >
                  This Month
                </button>
                <button
                  type="button"
                  @click="setDateFilter('lastMonth')"
                  :class="dateQuickFilterClass('lastMonth')"
                >
                  Last Month
                </button>
              </div>
            </div>

            <!-- Branch -->
            <div class="col-span-1">
              <label class="block text-xs font-medium text-gray-600">Branch</label>
              <select
                v-model="filters.branchId"
                class="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                @change="applySingleFilter('branchId')"
              >
                <option value="">All Branches</option>
                <option
                  v-for="branch in branchList"
                  :key="branch.id"
                  :value="branch.id"
                >
                  {{ branch.name }}
                </option>
              </select>
            </div>

            <!-- Customer Name/Phone -->
            <div class="col-span-1">
              <label class="block text-xs font-medium text-gray-600"
                >Customer Name</label
              >
              <input
                v-model="filters.customerName"
                type="text"
                @keyup.enter="applySingleFilter('customerName')"
                class="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div class="col-span-1">
              <label class="block text-xs font-medium text-gray-600"
                >Customer Phone</label
              >
              <input
                v-model="filters.customerPhone"
                type="text"
                @keyup.enter="applySingleFilter('customerPhone')"
                class="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <!-- Firm -->
            <div class="col-span-1">
              <label class="block text-xs font-medium text-gray-600"
                >Firm</label
              >
              <input
                v-model="filters.firm"
                type="text"
                @keyup.enter="applySingleFilter('firm')"
                class="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <!-- Identifier -->
            <div class="col-span-1">
              <label class="block text-xs font-medium text-gray-600"
                >Identifier</label
              >
              <input
                v-model="filters.identifier"
                type="text"
                @keyup.enter="applySingleFilter('identifier')"
                class="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <!-- Status -->
            <div class="col-span-1">
              <label class="block text-xs font-medium text-gray-600"
                >Service Request No.</label
              >
              <input
                v-model="filters.serviceRequestNumber"
                type="text"
                @keyup.enter="applySingleFilter('serviceRequestNumber')"
                class="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div class="col-span-1">
              <label class="block text-xs font-medium text-gray-600"
                >Category</label
              >
              <select
                v-model="filters.category"
                class="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Categories</option>

                <option
                  v-for="category in serviceCategoryList"
                  :key="category.serviceCategoryId"
                  :value="category.serviceCategoryId"
                >
                  {{ category.label }}
                </option>
              </select>
            </div>
            <div class="col-span-1">
              <label class="block text-xs font-medium text-gray-600"
                >Vendor
              </label>
              <select
                v-model="filters.vendor"
                class="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Vendors</option>

                <option
                  v-for="vendor in vendorList"
                  :key="vendor.vendorId"
                  :value="vendor.vendorId"
                >
                  {{ vendor.firstName }} {{ vendor.lastName }}
                </option>
              </select>
            </div>
            <!-- Identifier -->
           

            <!-- Status -->
            <div class="col-span-1">
              <label class="block text-xs font-medium text-gray-600"
                >Status</label
              >
              <select
                v-model="filters.status"
                class="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Statuses</option>
                <option value="Pending">Pending</option>
                <option value="Completed">Completed</option>
                <option value="Cancelled">Cancelled</option>
                <option value="Processing">Processing</option>
              </select>
            </div>

            <!-- Voucher Type -->
            <div class="col-span-1">
              <label class="block text-xs font-medium text-gray-600"
                >Voucher Type</label
              >
              <select
                v-model="filters.type"
                class="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Types</option>

                <option value="invoice">Invoice</option>
                <option value="chalan">Chalan</option>
                <option value="quotation">Quotation</option>
              </select>
            </div>

            <!-- Filter Actions -->
            <div class="space-y-2">
              <label class="block text-xs font-medium text-gray-600"
                >Actions</label
              >
              <div class="flex space-x-2">
                <button
                  @click="applyFilters"
                  class="flex-1 px-3 py-2 text-sm text-white bg-blue-600 rounded-md transition-colors hover:bg-blue-700"
                >
                  Apply
                </button>
                <button
                  @click="clearFilters"
                  class="flex-1 px-3 py-2 text-sm text-white bg-gray-500 rounded-md transition-colors hover:bg-gray-600"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>

          <!-- Active Filters Display -->
          <div
            v-if="hasActiveFilters"
            class="pt-4 mt-4 border-t border-gray-200"
          >
            <div class="flex justify-between items-center">
              <div class="flex items-center space-x-2">
                <span class="text-xs font-medium text-gray-600"
                  >Active Filters:</span
                >
                <div class="flex flex-wrap gap-2">
                  <span
                    v-if="appliedFilters.fromDate || appliedFilters.toDate"
                    class="inline-flex items-center px-2 py-1 text-xs text-blue-800 bg-blue-100 rounded-full"
                  >
                    Date: {{ formatAppliedDateRange() }}
                    <button
                      @click="clearDateFilter"
                      class="ml-1 text-blue-600 hover:text-blue-800"
                    >
                      ×
                    </button>
                  </span>
                  <span
                    v-if="appliedFilters.customerName"
                    class="inline-flex items-center px-2 py-1 text-xs text-green-800 bg-green-100 rounded-full"
                  >
                    Customer: {{ appliedFilters.customerName }}
                    <button
                      @click="clearAppliedFilter('customerName')"
                      class="ml-1 text-green-600 hover:text-green-800"
                    >
                      ×
                    </button>
                  </span>
                  <span
                    v-if="appliedFilters.customerPhone"
                    class="inline-flex items-center px-2 py-1 text-xs text-green-800 bg-green-100 rounded-full"
                  >
                    Phone: {{ appliedFilters.customerPhone }}
                    <button
                      @click="clearAppliedFilter('customerPhone')"
                      class="ml-1 text-green-600 hover:text-green-800"
                    >
                      ×
                    </button>
                  </span>
                  <span
                    v-if="appliedFilters.firm"
                    class="inline-flex items-center px-2 py-1 text-xs text-purple-800 bg-purple-100 rounded-full"
                  >
                    Firm: {{ appliedFilters.firm }}
                    <button
                      @click="clearAppliedFilter('firm')"
                      class="ml-1 text-purple-600 hover:text-purple-800"
                    >
                      ×
                    </button>
                  </span>
                  <span
                    v-if="appliedFilters.identifier"
                    class="inline-flex items-center px-2 py-1 text-xs text-orange-800 bg-orange-100 rounded-full"
                  >
                    ID: {{ appliedFilters.identifier }}
                    <button
                      @click="clearAppliedFilter('identifier')"
                      class="ml-1 text-orange-600 hover:text-orange-800"
                    >
                      ×
                    </button>
                  </span>
                  <span
                    v-if="appliedFilters.status"
                    class="inline-flex items-center px-2 py-1 text-xs text-red-800 bg-red-100 rounded-full"
                  >
                    Status: {{ appliedFilters.status }}
                    <button
                      @click="clearAppliedFilter('status')"
                      class="ml-1 text-red-600 hover:text-red-800"
                    >
                      ×
                    </button>
                  </span>
                  <span
                    v-if="appliedFilters.type"
                    class="inline-flex items-center px-2 py-1 text-xs text-indigo-800 bg-indigo-100 rounded-full"
                  >
                    Type: {{ formatVoucherType(appliedFilters.type) }}
                    <button
                      @click="clearAppliedFilter('type')"
                      class="ml-1 text-indigo-600 hover:text-indigo-800"
                    >
                      ×
                    </button>
                  </span>
                  <span
                    v-if="appliedFilters.serviceRequestNumber"
                    class="inline-flex items-center px-2 py-1 text-xs text-teal-800 bg-teal-100 rounded-full"
                  >
                    SR No: {{ appliedFilters.serviceRequestNumber }}
                    <button
                      @click="clearAppliedFilter('serviceRequestNumber')"
                      class="ml-1 text-teal-600 hover:text-teal-800"
                    >
                      ×
                    </button>
                  </span>
                  <span
                    v-if="appliedFilters.category"
                    class="inline-flex items-center px-2 py-1 text-xs text-pink-800 bg-pink-100 rounded-full"
                  >
                    Category: {{ getCategoryLabel(appliedFilters.category) }}
                    <button
                      @click="clearAppliedFilter('category')"
                      class="ml-1 text-pink-600 hover:text-pink-800"
                    >
                      ×
                    </button>
                  </span>
                  <span
                    v-if="appliedFilters.vendor"
                    class="inline-flex items-center px-2 py-1 text-xs text-cyan-800 bg-cyan-100 rounded-full"
                  >
                    Vendor: {{ getVendorLabel(appliedFilters.vendor) }}
                    <button
                      @click="clearAppliedFilter('vendor')"
                      class="ml-1 text-cyan-600 hover:text-cyan-800"
                    >
                      ×
                    </button>
                  </span>
                  <span
                    v-if="appliedFilters.branchId"
                    class="inline-flex items-center px-2 py-1 text-xs text-indigo-800 bg-indigo-100 rounded-full"
                  >
                    Branch: {{ getBranchLabel(appliedFilters.branchId) }}
                    <button
                      @click="clearAppliedFilter('branchId')"
                      class="ml-1 text-indigo-600 hover:text-indigo-800"
                    >
                      ×
                    </button>
                  </span>
                </div>
              </div>
              <span class="text-xs text-gray-500">
                <template v-if="loading">Loading vouchers…</template>
                <template v-else>
                  {{ pagination.from }}-{{ pagination.to }} of
                  {{ pagination.total }} vouchers
                </template>
              </span>
            </div>
          </div>
        </div>

        <!-- Static Header Row -->
        <div
          class="flex flex-shrink-0 items-center px-4 py-2 font-semibold text-gray-700 bg-gray-50 rounded-lg border border-gray-200"
        >
          <div class="pr-2 w-8 border-r border-gray-300">
            <input
              type="checkbox"
              v-model="selectAll"
              @change="handleSelectAll"
              class="w-4 h-4 text-blue-600 bg-gray-100 rounded border-gray-300 focus:ring-blue-500 focus:ring-2"
            />
          </div>
          <div class="px-2 border-r border-gray-300" style="width: 7%">
            Date
          </div>
          <div class="px-2 border-r border-gray-300" style="width: 6%">
            Branch
          </div>
          <div class="px-2 border-r border-gray-300" style="width: 12%">
            Customer
          </div>
          <div class="px-2 border-r border-gray-300" style="width: 9%">
            Firm
          </div>
          <div class="px-2 border-r border-gray-300" style="width: 7%">
            V Type
          </div>
          <div class="px-2 border-r border-gray-300" style="width: 8%">
            Identifier
          </div>
          <div class="px-2 border-r border-gray-300" style="width: 6%">
            SR No.
          </div>
          <div
            class="px-2 text-right border-r border-gray-300"
            style="width: 10%"
          >
            Amount
          </div>
          <div
            class="px-2 text-right border-r border-gray-300"
            style="width: 10%"
          >
            Received
          </div>
          <div
            class="px-2 text-right border-r border-gray-300"
            style="width: 7%"
          >
            Discount
          </div>
          <div
            class="px-2 text-right border-r border-gray-300"
            style="width: 7%"
          >
            Balance
          </div>
          <div class="px-2 border-r border-gray-300" style="width: 7%">
            Status
          </div>
          <div class="px-2" style="width: 4%"></div>
        </div>

        <!-- Scrollable Data Container -->
        <div class="overflow-y-auto relative flex-1 min-h-0">
          <div
            v-if="loading"
            class="flex absolute inset-0 z-10 flex-col justify-center items-center bg-white/80 backdrop-blur-[1px]"
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            <div
              class="w-10 h-10 rounded-full border-2 border-gray-200 animate-spin border-t-blue-600"
            ></div>
            <span class="mt-3 text-sm font-medium text-gray-600"
              >Loading vouchers…</span
            >
          </div>

          <template v-if="!loading">
            <!-- Data Rows -->
            <div
              v-for="(voucher, index) in voucherList"
              :key="index"
              class="flex items-center px-4 border-b border-gray-200 transition-colors duration-150 hover:bg-gray-50"
              :class="{
                'bg-blue-50 border-blue-200': selectedRows.includes(index)
              }"
            >
              <div class="pr-2 w-8 border-r border-gray-300">
                <input
                  type="checkbox"
                  :value="index"
                  v-model="selectedRows"
                  @change="handleRowSelect"
                  class="w-4 h-4 text-blue-600 bg-gray-100 rounded border-gray-300 focus:ring-blue-500 focus:ring-2"
                />
              </div>
              <div
                class="px-2 text-sm text-gray-600 border-r border-gray-300"
                style="width: 7%"
              >
                {{ formatDate(voucher.date) }}
              </div>
              <div
                class="px-2 text-sm text-gray-600 truncate border-r border-gray-300"
                style="width: 6%"
              >
                <span
                  v-if="voucher.branchName"
                  class="inline-flex px-1 py-0.5 text-xs font-semibold rounded-full"
                  :style="branchBadgeStyle(voucher.branchColor)"
                >
                  {{ voucher.branchName }}
                </span>
                <span v-else>-</span>
              </div>
              <div
                class="px-2 text-sm font-medium text-gray-900 truncate border-r border-gray-300"
                style="width: 12%"
              >
                {{ voucher.customerName }}
              </div>
              <div
                class="px-2 text-sm text-gray-600 truncate border-r border-gray-300"
                style="width: 9%"
              >
                {{ voucher.firm }}
              </div>
              <div
                class="flex gap-1 items-center px-2 text-sm text-gray-600 border-r border-gray-300"
                style="width: 7%"
              >
                <span class="truncate">{{
                  formatVoucherType(voucher.voucherType)
                }}</span>
                <span
                  v-if="chalanDirectionLabel(voucher)"
                  class="inline-flex px-1 py-0.5 text-xs font-semibold rounded-full whitespace-nowrap"
                  :class="
                    voucher.isInward
                      ? 'text-red-800 bg-red-100'
                      : 'text-green-800 bg-green-100'
                  "
                >
                  {{ chalanDirectionLabel(voucher) }}
                </span>
              </div>
              <div
                class="px-2 font-mono text-sm text-gray-600 border-r border-gray-300"
                style="width: 8%"
              >
                {{ voucher.identifier }}
              </div>
              <div
                class="px-2 text-sm text-gray-600 truncate border-r border-gray-300"
                style="width: 6%"
              >
                <router-link
                  v-if="
                    voucher.serviceRequestId && voucher.serviceRequestNumber
                  "
                  :to="`/serviceRequests/flow/${voucher.serviceRequestId}`"
                  class="text-blue-600 underline hover:text-blue-800"
                  target="_blank"
                >
                  {{ voucher.serviceRequestNumber }}
                </router-link>
                <span v-else>{{ voucher.serviceRequestNumber || "-" }}</span>
              </div>
              <div
                class="px-2 text-sm font-medium text-right text-gray-900 border-r border-gray-300"
                style="width: 10%"
              >
                ₹{{ formatAmount(voucher.amount) }}
              </div>
              <div
                class="px-2 text-sm text-right text-gray-600 border-r border-gray-300"
                style="width: 10%"
              >
                ₹{{ formatAmount(voucher.received) }}
              </div>
              <div
                class="px-2 text-sm text-right text-gray-600 border-r border-gray-300"
                style="width: 7%"
              >
                ₹{{ formatAmount(voucher.discount) }}
              </div>
              <div
                class="px-2 text-sm font-medium text-right border-r border-gray-300"
                style="width: 7%"
                :class="voucher.balance > 0 ? 'text-red-600' : 'text-green-600'"
              >
                ₹{{ formatAmount(voucher.balance) }}
              </div>
              <div
                class="px-2 text-xs border-r border-gray-300"
                style="width: 7%"
              >
                <el-tag :type="getStatusType(voucher.status)" size="small">
                  {{ voucher.status }}
                </el-tag>
              </div>
              <div class="flex justify-center items-center py-1" style="width: 4%">
                <div class="inline-block relative text-left dropdown-container">
                  <div>
                    <button
                      type="button"
                      class="inline-flex justify-center items-center w-7 h-7 text-gray-500 rounded-full transition-colors duration-150 hover:bg-gray-100 hover:text-gray-700"
                      :class="voucher.isDropdownOpen ? 'bg-gray-100 text-gray-700' : ''"
                      aria-expanded="true"
                      aria-haspopup="true"
                      @click="handleDropdown(voucher, index)"
                      title="Actions"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                        <circle cx="12" cy="5" r="1.5"/>
                        <circle cx="12" cy="12" r="1.5"/>
                        <circle cx="12" cy="19" r="1.5"/>
                      </svg>
                    </button>
                  </div>
                  <div
                    v-if="voucher.isDropdownOpen"
                    class="absolute right-0 z-50 mt-2 w-48 bg-white rounded-lg border border-gray-200 shadow-xl"
                    role="menu"
                    aria-orientation="vertical"
                    aria-labelledby="menu-button"
                    tabindex="-1"
                    style="top: 100%;"
                  >
                    <div class="py-2" role="none">
                      <button
                        v-if="voucher.voucherType === 'invoice'"
                        @click="handlePayment(voucher, index)"
                        class="flex items-center px-4 py-2 w-full text-sm text-gray-700 transition-colors duration-150 hover:bg-blue-50 hover:text-blue-700"
                        role="menuitem"
                        tabindex="-1"
                        id="menu-item-0"
                      >
                        Make Payment
                      </button>
                      <button
                        v-if="hasPaymentHistory(voucher)"
                        @click="handlePaymentHistory(voucher, index)"
                        class="flex items-center px-4 py-2 w-full text-sm text-gray-700 transition-colors duration-150 hover:bg-blue-50 hover:text-blue-700"
                        role="menuitem"
                        tabindex="-1"
                        id="menu-item-1"
                      >
                        Payment History
                      </button>
                      <button
                        v-if="voucher.voucherType === 'invoice'"
                        @click="handleViewSendInvoice(voucher, index)"
                        class="flex items-center px-4 py-2 w-full text-sm text-gray-700 transition-colors duration-150 hover:bg-blue-50 hover:text-blue-700"
                        role="menuitem"
                        tabindex="-1"
                        id="menu-item-2"
                      >
                        View / Send Invoice
                      </button>
                      <button
                        v-if="voucher.voucherType === 'quotation'"
                        @click="handleViewSendQuotation(voucher, index)"
                        class="flex items-center px-4 py-2 w-full text-sm text-gray-700 transition-colors duration-150 hover:bg-blue-50 hover:text-blue-700"
                        role="menuitem"
                        tabindex="-1"
                        id="menu-item-3"
                      >
                        View / Send Quotation
                      </button>
                      <button
                        v-if="isChalanVoucher(voucher)"
                        @click="handleViewSendChalan(voucher, index)"
                        class="flex items-center px-4 py-2 w-full text-sm text-gray-700 transition-colors duration-150 hover:bg-blue-50 hover:text-blue-700"
                        role="menuitem"
                        tabindex="-1"
                        id="menu-item-4"
                      >
                        View / Send Chalan
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Empty State -->
            <div
              v-if="voucherList.length === 0"
              class="flex flex-col justify-center items-center py-12"
            >
              <div class="mb-4 text-gray-400">
                <svg
                  class="mx-auto w-12 h-12"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <p class="text-lg text-gray-500">No vouchers found</p>
              <p class="mt-1 text-sm text-gray-400">
                Try adjusting your filters or create a new voucher
              </p>
            </div>
          </template>
        </div>

        <!-- Static Summary Rows -->
        <!-- Selected Rows Summary -->
        <div
          v-if="selectedRows.length > 0"
          class="flex flex-shrink-0 items-center px-4 py-2 font-semibold bg-blue-50 border-b-2 border-blue-200"
        >
          <div class="pr-2 w-8 border-r border-gray-300">
            <span class="text-xs text-blue-600">{{ selectedRows.length }}</span>
          </div>
          <div
            class="px-2 text-sm text-blue-800 border-r border-gray-300"
            style="width: 7%"
          >
            Selected
          </div>
          <div
            class="px-2 text-sm text-blue-800 border-r border-gray-300"
            style="width: 6%"
          >
            -
          </div>
          <div
            class="px-2 text-sm text-blue-800 border-r border-gray-300"
            style="width: 12%"
          >
            {{ selectedRows.length }} items
          </div>
          <div
            class="px-2 text-sm text-blue-800 border-r border-gray-300"
            style="width: 9%"
          >
            -
          </div>
          <div
            class="px-2 text-sm text-blue-800 border-r border-gray-300"
            style="width: 7%"
          >
            -
          </div>
          <div
            class="px-2 text-sm text-blue-800 border-r border-gray-300"
            style="width: 8%"
          >
            -
          </div>
          <div
            class="px-2 text-sm text-blue-800 border-r border-gray-300"
            style="width: 6%"
          >
            -
          </div>
          <div
            class="px-2 text-sm font-bold text-right text-blue-800 border-r border-gray-300"
            style="width: 10%"
          >
            ₹{{ formatAmount(selectedTotals.amount) }}
          </div>
          <div
            class="px-2 text-sm font-bold text-right text-blue-800 border-r border-gray-300"
            style="width: 10%"
          >
            ₹{{ formatAmount(selectedTotals.received) }}
          </div>
          <div
            class="px-2 text-sm font-bold text-right text-blue-800 border-r border-gray-300"
            style="width: 7%"
          >
            ₹{{ formatAmount(selectedTotals.discount) }}
          </div>
          <div
            class="px-2 text-sm font-bold text-right border-r border-gray-300"
            style="width: 7%"
            :class="
              selectedTotals.balance > 0 ? 'text-red-600' : 'text-green-600'
            "
          >
            ₹{{ formatAmount(selectedTotals.balance) }}
          </div>
          <div class="px-2 text-xs border-r border-gray-300" style="width: 7%">
            -
          </div>
          <div class="px-2" style="width: 4%">
            <button
              v-if="selectedInvoices.length > 1"
              @click="handleMultiplePayment"
              class="px-3 py-1 text-xs text-white bg-green-600 rounded hover:bg-green-700"
            >
              Make Payment
            </button>
          </div>
        </div>

        <!-- Total Summary Row -->
        <div
          v-if="!loading && voucherList.length > 0"
          class="flex flex-shrink-0 items-center px-4 py-3 font-bold bg-gray-100 border-t-2 border-gray-300"
        >
          <div class="pr-2 w-8 border-r border-gray-300">
            <span class="text-xs text-gray-600">{{ voucherList.length }}</span>
          </div>
          <div
            class="px-2 text-sm text-gray-800 border-r border-gray-300"
            style="width: 7%"
          >
            Total
          </div>
          <div
            class="px-2 text-sm text-gray-800 border-r border-gray-300"
            style="width: 6%"
          >
            -
          </div>
          <div
            class="px-2 text-sm text-gray-800 border-r border-gray-300"
            style="width: 12%"
          >
            {{ summary.total_vouchers }} vouchers
          </div>
          <div
            class="px-2 text-sm text-gray-800 border-r border-gray-300"
            style="width: 9%"
          >
            -
          </div>
          <div
            class="px-2 text-sm text-gray-800 border-r border-gray-300"
            style="width: 7%"
          >
            -
          </div>
          <div
            class="px-2 text-sm text-gray-800 border-r border-gray-300"
            style="width: 8%"
          >
            -
          </div>
          <div
            class="px-2 text-sm text-gray-800 border-r border-gray-300"
            style="width: 6%"
          >
            -
          </div>
          <div
            class="px-2 text-sm font-bold text-right text-gray-900 border-r border-gray-300"
            style="width: 10%"
          >
            ₹{{ formatAmount(summary.total_amount) }}
          </div>
          <div
            class="px-2 text-sm font-bold text-right text-gray-900 border-r border-gray-300"
            style="width: 10%"
          >
            ₹{{ formatAmount(summary.total_received) }}
          </div>
          <div
            class="px-2 text-sm font-bold text-right text-gray-900 border-r border-gray-300"
            style="width: 7%"
          >
            ₹{{ formatAmount(summary.total_discount) }}
          </div>
          <div
            class="px-2 text-sm font-bold text-right border-r border-gray-300"
            style="width: 7%"
            :class="
              summary.total_balance > 0 ? 'text-red-600' : 'text-green-600'
            "
          >
            ₹{{ formatAmount(summary.total_balance) }}
          </div>
          <div class="px-2 text-xs border-r border-gray-300" style="width: 7%">
            -
          </div>
          <div class="px-2" style="width: 4%">
            -
          </div>
        </div>

        <!-- Static Pagination -->
        <div
          v-if="!loading && voucherList.length > 0"
          class="flex flex-shrink-0 justify-between items-center mt-2"
        >
          <div class="flex items-center space-x-2">
            <label for="per-page" class="text-sm font-medium text-gray-700"
              >Records per page:</label
            >
            <select
              id="per-page"
              v-model="pagination.per_page"
              @change="handlePerPageChange"
              class="px-2 py-1 text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>

          <div class="flex items-center space-x-2">
            <button
              @click="goToPage(pagination.current_page - 1)"
              :disabled="pagination.current_page <= 1"
              class="px-3 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>

            <div class="flex space-x-1">
              <button
                v-for="page in getVisiblePages()"
                :key="page"
                @click="goToPage(page)"
                :class="[
                  'px-3 py-2 text-sm border rounded-md',
                  page === pagination.current_page
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'border-gray-300 hover:bg-gray-50'
                ]"
              >
                {{ page }}
              </button>
            </div>

            <button
              @click="goToPage(pagination.current_page + 1)"
              :disabled="pagination.current_page >= pagination.last_page"
              class="px-3 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Invoice Payment Popup -->
    <invoice-payment-popup
      :show.sync="showPaymentPopup"
      :selected-invoices="paymentInvoices"
      :customer-name="paymentCustomerName"
      :firm-name="paymentFirmName"
      :saving="savingPayment"
      :dual-firm-mode="paymentDualFirm"
      :service-request-number="paymentServiceRequestNumber"
      :rajput-invoice="paymentRajputInvoice"
      :rajdoot-invoice="paymentRajdootInvoice"
      @save="handleSavePayment"
    />

    <payment-history-popup
      :show.sync="showPaymentHistoryPopup"
      :loading="loadingPaymentHistory"
      :identifier="paymentHistoryIdentifier"
      :firm="paymentHistoryFirm"
      :transactions="paymentHistoryTransactions"
      :total-received="paymentHistoryTotal"
    />

    <el-dialog
      :visible.sync="showWhatsappSaleModal"
      @open="onSaleInvoiceDialogOpen"
      @close="onSaleInvoiceDialogClose"
      width="60%"
      title="Sale Invoice"
    >
      <whatsapp-bill-print ref="saleInvoice" />
    </el-dialog>

    <el-dialog
      :visible.sync="showWhatsappVendorModal"
      @open="onVendorInvoiceDialogOpen"
      @close="onVendorInvoiceDialogClose"
      width="60%"
      title="Vendor Invoice"
    >
      <vendor-invoice ref="vendorInvoice" />
    </el-dialog>

    <el-dialog
      :visible.sync="showWhatsappQuotationModal"
      width="60%"
      title="Sale Quotation"
      :append-to-body="true"
      @close="onQuotationDialogClose"
    >
      <SaleQuotationDesign
        v-if="showWhatsappQuotationModal && quotationData"
        :data="quotationData"
        @close="showWhatsappQuotationModal = false"
      />
    </el-dialog>

    <el-dialog
      :visible.sync="showWhatsappChalanModal"
      width="60%"
      title="Chalan"
      :append-to-body="true"
      @close="onChalanDialogClose"
    >
      <ChalanDesign
        v-if="showWhatsappChalanModal && chalanData"
        :data="chalanData"
        @close="showWhatsappChalanModal = false"
      />
    </el-dialog>
  </div>
</template>

<script>
import endpoints from "../../utils/constants";
import { apiHelper } from "../../utils/helpers";
import snakeToCamel from "../../utils/SnakeToCamel";
import { Tag, Dialog } from "element-ui";
import InvoicePaymentPopup from "@/components/InvoicePaymentPopup.vue";
import PaymentHistoryPopup from "@/components/PaymentHistoryPopup.vue";
import WhatsAppBillPrint from "../Pos/WhatsAppBillPrint.vue";
import VendorInvoiceDesign from "../ServiceRequest/VendorInvoiceDesign.vue";
import SaleQuotationDesign from "../Pos/SaleQuotationDesign.vue";
import ChalanDesign from "../ServiceRequestChalan/ChalanDesign.vue";
import { mapActions, mapMutations } from "vuex";
import {
  splitServiceRequestInvoices,
  classifyFirm
} from "../../utils/firmHelpers";

/** YYYY-MM-DD in local time; toISOString() would shift month/day boundaries in IST. */
function formatLocalDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Calendar month range. `offset`: 0 = current month, -1 = previous month. */
function calendarMonthRange(offset = 0, refDate = new Date()) {
  const y = refDate.getFullYear();
  const m = refDate.getMonth() + offset;
  return {
    fromDate: formatLocalDate(new Date(y, m, 1)),
    toDate: formatLocalDate(new Date(y, m + 1, 0))
  };
}

/** Indian FY: 1 Apr → 31 Mar. `period`: 'current' | 'prev' */
function indianFinancialYearRange(period = "current", refDate = new Date()) {
  const y = refDate.getFullYear();
  const month = refDate.getMonth();
  let fyStartYear = month >= 3 ? y : y - 1;
  if (period === "prev") fyStartYear -= 1;
  return {
    fromDate: `${fyStartYear}-04-01`,
    toDate: `${fyStartYear + 1}-03-31`
  };
}

export default {
  name: "VoucherList",
  components: {
    [Tag.name]: Tag,
    [Dialog.name]: Dialog,
    InvoicePaymentPopup,
    PaymentHistoryPopup,
    WhatsAppBillPrint,
    VendorInvoiceDesign,
    SaleQuotationDesign,
    ChalanDesign,
    "invoice-payment-popup": InvoicePaymentPopup,
    "payment-history-popup": PaymentHistoryPopup,
    "whatsapp-bill-print": WhatsAppBillPrint,
    "vendor-invoice": VendorInvoiceDesign,
    "sale-quotation-design": SaleQuotationDesign,
    "chalan-design": ChalanDesign
  },
  data() {
    const defaultFY = indianFinancialYearRange("current");
    return {
      selectAll: false,
      isIndeterminate: false,
      selectedRows: [],
      voucherList: [],
      pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 20,
        total: 0,
        from: 0,
        to: 0
      },
      summary: {
        total_amount: 0,
        total_received: 0,
        total_discount: 0,
        total_balance: 0,
        total_vouchers: 0
      },
      loading: false,
      filters: {
        fromDate: defaultFY.fromDate,
        toDate: defaultFY.toDate,
        customerName: "",
        customerPhone: "",
        firm: "",
        identifier: "",
        status: "",
        serviceRequestNumber: "",
        type: "",
        category: "",
        vendor: "",
        branchId: ""
      },
      appliedFilters: {
        fromDate: defaultFY.fromDate,
        toDate: defaultFY.toDate,
        customerName: "",
        customerPhone: "",
        firm: "",
        identifier: "",
        status: "",
        serviceRequestNumber: "",
        type: "",
        category: "",
        vendor: "",
        branchId: ""
      },
      serviceCategoryList: [],
      vendorList: [],
      branchList: [],
      showPaymentPopup: false,
      savingPayment: false,
      paymentInvoices: [],
      paymentDualFirm: false,
      paymentServiceRequestNumber: "",
      paymentCustomerName: "",
      paymentFirmName: "",
      paymentRajputInvoice: null,
      paymentRajdootInvoice: null,
      showPaymentHistoryPopup: false,
      loadingPaymentHistory: false,
      paymentHistoryIdentifier: "",
      paymentHistoryFirm: "",
      paymentHistoryTransactions: [],
      paymentHistoryTotal: 0,
      showWhatsappSaleModal: false,
      showWhatsappVendorModal: false,
      showWhatsappQuotationModal: false,
      showWhatsappChalanModal: false,
      quotationData: null,
      chalanData: null,
      /** When true, filter watchers sync state but do not fetch (avoids duplicate calls on bulk clear). */
      _skipFilterWatchFetch: false,
      /** Incremented per vouchers fetch; only the latest response is applied. */
      voucherRequestId: 0
    };
  },
  computed: {
    hasActiveFilters() {
      return Object.values(this.appliedFilters).some(value => value !== "");
    },
    selectedTotals() {
      if (this.selectedRows.length === 0) {
        return { amount: 0, received: 0, discount: 0, balance: 0 };
      }

      return this.selectedRows.reduce(
        (totals, index) => {
          const voucher = this.voucherList[index];
          if (voucher) {
            totals.amount += parseFloat(voucher.amount) || 0;
            totals.received += parseFloat(voucher.received) || 0;
            totals.discount += parseFloat(voucher.discount) || 0;
            totals.balance += parseFloat(voucher.balance) || 0;
          }
          return totals;
        },
        { amount: 0, received: 0, discount: 0, balance: 0 }
      );
    },
    selectedInvoices() {
      // Get selected invoices (only invoice type vouchers)
      return this.selectedRows
        .map(index => this.voucherList[index])
        .filter(
          voucher =>
            voucher && voucher.voucherType === "invoice" && voucher.balance > 0
        );
    },
    /** Which date quick-filter matches `filters` (same rules as setDateFilter). */
    activeDatePreset() {
      const f = this.filters.fromDate;
      const t = this.filters.toDate;
      if (!f || !t) return null;

      const cur = indianFinancialYearRange("current");
      if (f === cur.fromDate && t === cur.toDate) return "currentFY";

      const prev = indianFinancialYearRange("prev");
      if (f === prev.fromDate && t === prev.toDate) return "prevFY";

      const today = new Date();
      const todayStr = formatLocalDate(today);
      if (f === todayStr && t === todayStr) return "today";

      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = formatLocalDate(yesterday);
      if (f === yesterdayStr && t === yesterdayStr) return "yesterday";

      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay());
      const weekStartStr = formatLocalDate(startOfWeek);
      if (f === weekStartStr && t === todayStr) return "thisWeek";

      const thisMonth = calendarMonthRange(0, today);
      if (f === thisMonth.fromDate && t === thisMonth.toDate)
        return "thisMonth";

      const lastMonth = calendarMonthRange(-1, today);
      if (f === lastMonth.fromDate && t === lastMonth.toDate)
        return "lastMonth";

      return null;
    }
  },
  watch: {
    "filters.fromDate"(newVal) {
      this.appliedFilters.fromDate = newVal;
      this.pagination.current_page = 1;
      if (!this._skipFilterWatchFetch) this.fetchVouchers();
    },
    "filters.toDate"(newVal) {
      this.appliedFilters.toDate = newVal;
      this.pagination.current_page = 1;
      if (!this._skipFilterWatchFetch) this.fetchVouchers();
    },
    "filters.status"(newVal) {
      this.appliedFilters.status = newVal;
      this.pagination.current_page = 1;
      if (!this._skipFilterWatchFetch) this.fetchVouchers();
    },
    "filters.vendor"(newVal) {
      this.appliedFilters.vendor = newVal;
      this.pagination.current_page = 1;
      if (!this._skipFilterWatchFetch) this.fetchVouchers();
    },
    "filters.category"(newVal) {
      this.appliedFilters.category = newVal;
      this.pagination.current_page = 1;
      if (!this._skipFilterWatchFetch) this.fetchVouchers();
    },
    "filters.type"(newVal) {
      this.appliedFilters.type = newVal;
      this.pagination.current_page = 1;
      if (!this._skipFilterWatchFetch) this.fetchVouchers();
    }
  },
  mounted() {
    this.fetchVouchers();
    this.fetchServiceCategoryList();
    this.fetchVendorList();
    this.fetchBranchList();
    this.getPaymentModes();
  },
  methods: {
    ...mapActions({
      getPaymentModes: "paymentMode/getPaymentModes",
      getReceiptDataForWhatsapp: "sale/getReceiptDataForWhatsapp",
      getServiceRequestLabors: "serviceRequestLabor/getServiceRequestLabors",
      fetchServiceRequest: "serviceRequest/fetchServiceRequest",
      getUserQuotation: "user/getUserQuotation",
      getServiceRequestChalanDetails:
        "serviceRequestChalan/getServiceRequestChalanDetails"
    }),
    ...mapMutations({
      setVendorInvoiceData: "vendor/setVendorInvoiceData"
    }),
    async fetchServiceCategoryList() {
      const response = await apiHelper.call(
        "get",
        `${endpoints.BASE_URL}${endpoints.API.serviceCategories}`
      );
      this.serviceCategoryList = response.data;
    },
    async fetchVendorList() {
      const response = await apiHelper.call(
        "get",
        `${endpoints.BASE_URL}${endpoints.API.vendors}`
      );
      this.vendorList = response.data;
    },
    async fetchBranchList() {
      const response = await apiHelper.call(
        "get",
        `${endpoints.LARAVEL_BASE_URL}/api/v1/branches`
      );
      this.branchList = response.data.data || response.data;
    },
    async fetchVouchers() {
      // Only the newest request may write to the table; overlapping fetches can
      // otherwise resolve out of order and leave stale rows on screen.
      const requestId = ++this.voucherRequestId;
      this.loading = true;
      try {
        const params = new URLSearchParams();
        params.append("page", this.pagination.current_page);
        params.append("per_page", this.pagination.per_page);

        // Add filters to params
        if (this.appliedFilters.fromDate)
          params.append("from_date", this.appliedFilters.fromDate);
        if (this.appliedFilters.toDate)
          params.append("to_date", this.appliedFilters.toDate);
        if (this.appliedFilters.customerName)
          params.append("customer_name", this.appliedFilters.customerName);
        if (this.appliedFilters.customerPhone)
          params.append("customer_phone", this.appliedFilters.customerPhone);
        if (this.appliedFilters.firm)
          params.append("firm", this.appliedFilters.firm);
        if (this.appliedFilters.identifier)
          params.append("identifier", this.appliedFilters.identifier);
        if (this.appliedFilters.status)
          params.append("status", this.appliedFilters.status);
        if (this.appliedFilters.serviceRequestNumber)
          params.append(
            "service_request_number",
            this.appliedFilters.serviceRequestNumber
          );
        if (this.appliedFilters.vendor)
          params.append("vendor", this.appliedFilters.vendor);
        if (this.appliedFilters.category)
          params.append("category", this.appliedFilters.category);
        if (this.appliedFilters.type)
          params.append("type", this.appliedFilters.type);
        if (this.appliedFilters.branchId)
          params.append("branch_id", this.appliedFilters.branchId);

        const response = await apiHelper.call(
          "get",
          `${endpoints.LARAVEL_BASE_URL}/api/v1/vouchers?${params.toString()}`
        );

        if (requestId !== this.voucherRequestId) return;

        this.voucherList = response.data.data.map(voucher =>
          this.mapVoucherRow(voucher)
        );

        this.pagination = {
          current_page: response.data.pagination.current_page,
          last_page: response.data.pagination.last_page,
          per_page: response.data.pagination.per_page,
          total: response.data.pagination.total,
          from: response.data.pagination.from,
          to: response.data.pagination.to
        };

        // Update summary from API response
        if (response.data.summary) {
          const totalAmount = response.data.summary.total_amount || 0;
          const totalReceived = response.data.summary.total_received || 0;
          const totalDiscount = response.data.summary.total_discount || 0;
          const totalVouchers = response.data.summary.total_vouchers || 0;

          this.summary = {
            total_amount: totalAmount,
            total_received: totalReceived,
            total_discount: totalDiscount,
            total_balance: totalAmount - totalReceived - totalDiscount,
            total_vouchers: totalVouchers
          };
        }
      } catch (error) {
        console.error("Error fetching vouchers:", error);
        if (requestId === this.voucherRequestId) {
          this.$message.error("Failed to fetch vouchers");
        }
      } finally {
        if (requestId === this.voucherRequestId) {
          this.loading = false;
        }
      }
    },
    goToPage(page) {
      if (page >= 1 && page <= this.pagination.last_page) {
        this.pagination.current_page = page;
        this.fetchVouchers();
      }
    },
    handlePerPageChange() {
      this.pagination.current_page = 1;
      this.fetchVouchers();
    },
    getVisiblePages() {
      const current = this.pagination.current_page;
      const last = this.pagination.last_page;
      const delta = 2;
      const range = [];
      const rangeWithDots = [];

      for (
        let i = Math.max(2, current - delta);
        i <= Math.min(last - 1, current + delta);
        i++
      ) {
        range.push(i);
      }

      if (current - delta > 2) {
        rangeWithDots.push(1, "...");
      } else {
        rangeWithDots.push(1);
      }

      rangeWithDots.push(...range);

      if (current + delta < last - 1) {
        rangeWithDots.push("...", last);
      } else if (last > 1) {
        rangeWithDots.push(last);
      }

      return rangeWithDots;
    },
    handleSelectAll() {
      this.selectedRows = this.selectAll
        ? this.voucherList.map((_, index) => index)
        : [];
      this.isIndeterminate = false;
    },
    handleRowSelect() {
      const selectedCount = this.selectedRows.length;
      this.selectAll = selectedCount === this.voucherList.length;
      this.isIndeterminate =
        selectedCount > 0 && selectedCount < this.voucherList.length;
    },
    formatDate(date) {
      if (!date) return "";
      return new Date(date).toLocaleDateString("en-IN");
    },
    formatVoucherType(type) {
      const labels = {
        invoice: "Invoice",
        chalan: "Chalan",
        customer_chalan: "Chalan",
        quotation: "Quotation"
      };
      return labels[type] || type || "-";
    },
    formatAmount(amount) {
      return new Intl.NumberFormat("en-IN").format(amount);
    },
    getStatusType(status) {
      const statusTypes = {
        Pending: "warning",
        Completed: "success",
        Cancelled: "danger",
        Processing: "info"
      };
      return statusTypes[status] || "info";
    },
    handleDropdown(voucher, index) {
      // Close all other dropdowns first
      this.voucherList = this.voucherList.map((item, i) => ({
        ...item,
        isDropdownOpen: i === index ? !item.isDropdownOpen : false
      }));
    },
    handleView(voucher, index) {
      // Close the dropdown
      this.handleDropdown(voucher, index);
      this.$message.info(`Viewing voucher: ${voucher.identifier}`);
      // Add your view logic here
    },
    handleEdit(voucher, index) {
      // Close the dropdown
      this.handleDropdown(voucher, index);
      this.$message.info(`Editing voucher: ${voucher.identifier}`);
      // Add your edit logic here
    },
    handleDelete(voucher, index) {
      // Close the dropdown
      this.handleDropdown(voucher, index);
      this.$confirm(
        "Are you sure you want to delete this voucher?",
        "Confirm Delete",
        {
          confirmButtonText: "Yes",
          cancelButtonText: "No",
          type: "warning"
        }
      )
        .then(() => {
          this.voucherList.splice(index, 1);
          this.$message.success("Voucher deleted successfully");
        })
        .catch(() => {
          this.$message.info("Delete cancelled");
        });
    },
    dateQuickFilterClass(preset) {
      const selected = this.activeDatePreset === preset;
      return [
        "px-2 py-1 text-xs rounded transition-colors",
        selected
          ? "text-blue-700 bg-blue-100 hover:bg-blue-200"
          : "text-gray-700 bg-gray-100 hover:bg-gray-200"
      ];
    },
    dateFilterRange(type) {
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay());

      switch (type) {
        case "currentFY":
          return indianFinancialYearRange("current");
        case "prevFY":
          return indianFinancialYearRange("prev");
        case "today":
          return {
            fromDate: formatLocalDate(today),
            toDate: formatLocalDate(today)
          };
        case "yesterday":
          return {
            fromDate: formatLocalDate(yesterday),
            toDate: formatLocalDate(yesterday)
          };
        case "thisWeek":
          return {
            fromDate: formatLocalDate(startOfWeek),
            toDate: formatLocalDate(today)
          };
        case "thisMonth":
          return calendarMonthRange(0, today);
        case "lastMonth":
          return calendarMonthRange(-1, today);
        default:
          return null;
      }
    },
    setDateFilter(type) {
      const range = this.dateFilterRange(type);
      if (!range) return;

      // Set both dates behind the watcher guard so the pair is fetched once;
      // fetching per field would race a request built from the stale other date.
      this._skipFilterWatchFetch = true;
      this.filters.fromDate = range.fromDate;
      this.filters.toDate = range.toDate;
      this.$nextTick(() => {
        this._skipFilterWatchFetch = false;
        this.pagination.current_page = 1;
        this.fetchVouchers();
      });
    },
    clearDateFilter() {
      this._skipFilterWatchFetch = true;
      this.filters.fromDate = "";
      this.filters.toDate = "";
      this.$nextTick(() => {
        this._skipFilterWatchFetch = false;
        this.pagination.current_page = 1;
        this.fetchVouchers();
      });
    },
    formatDateRange() {
      if (this.filters.fromDate && this.filters.toDate) {
        if (this.filters.fromDate === this.filters.toDate) {
          return this.formatDate(this.filters.fromDate);
        } else {
          return `${this.formatDate(this.filters.fromDate)} - ${this.formatDate(
            this.filters.toDate
          )}`;
        }
      } else if (this.filters.fromDate) {
        return `From ${this.formatDate(this.filters.fromDate)}`;
      } else if (this.filters.toDate) {
        return `Until ${this.formatDate(this.filters.toDate)}`;
      }
      return "";
    },
    formatAppliedDateRange() {
      if (this.appliedFilters.fromDate && this.appliedFilters.toDate) {
        if (this.appliedFilters.fromDate === this.appliedFilters.toDate) {
          return this.formatDate(this.appliedFilters.fromDate);
        } else {
          return `${this.formatDate(
            this.appliedFilters.fromDate
          )} - ${this.formatDate(this.appliedFilters.toDate)}`;
        }
      } else if (this.appliedFilters.fromDate) {
        return `From ${this.formatDate(this.appliedFilters.fromDate)}`;
      } else if (this.appliedFilters.toDate) {
        return `Until ${this.formatDate(this.appliedFilters.toDate)}`;
      }
      return "";
    },
    getCategoryLabel(categoryId) {
      const category = this.serviceCategoryList.find(
        cat => cat.serviceCategoryId === categoryId
      );
      return category ? category.label : categoryId;
    },
    getVendorLabel(vendorId) {
      const vendor = this.vendorList.find(v => v.vendorId === vendorId);
      return vendor ? `${vendor.firstName} ${vendor.lastName}` : vendorId;
    },
    getBranchLabel(branchId) {
      const branch = this.branchList.find(b => b.id === branchId);
      return branch ? branch.name : branchId;
    },
    clearAppliedFilter(filterName) {
      this.appliedFilters[filterName] = "";
      this.filters[filterName] = "";
      this.pagination.current_page = 1;
      this.fetchVouchers();
    },
    applyFilters() {
      // Copy current filter values to applied filters
      this.appliedFilters = { ...this.filters };
      this.pagination.current_page = 1;
      this.fetchVouchers();
    },
    applySingleFilter(filterName) {
      // Apply only the specified filter while keeping all other applied filters
      this.appliedFilters[filterName] = this.filters[filterName];
      this.pagination.current_page = 1;
      this.fetchVouchers();
    },
    clearFilters() {
      this._skipFilterWatchFetch = true;
      this.filters = {
        fromDate: "",
        toDate: "",
        customerName: "",
        customerPhone: "",
        firm: "",
        identifier: "",
        status: "",
        serviceRequestNumber: "",
        type: "",
        category: "",
        vendor: "",
        branchId: ""
      };
      this.appliedFilters = {
        fromDate: "",
        toDate: "",
        customerName: "",
        customerPhone: "",
        firm: "",
        identifier: "",
        status: "",
        serviceRequestNumber: "",
        type: "",
        category: "",
        vendor: "",
        branchId: ""
      };
      this.$nextTick(() => {
        this._skipFilterWatchFetch = false;
        this.pagination.current_page = 1;
        this.fetchVouchers();
      });
    },
    branchBadgeStyle(color) {
      if (!color) return { background: '#dbeafe', color: '#1e40af' };
      return { background: color, color: '#fff' };
    },
    mapVoucherRow(voucher) {
      const amount = parseFloat(voucher.amount) || 0;
      const received = parseFloat(voucher.received) || 0;
      const discount = parseFloat(voucher.discount) || 0;
      return {
        ...snakeToCamel(voucher),
        balance: amount - received - discount,
        isDropdownOpen: false
      };
    },
    resetPaymentContext() {
      this.paymentInvoices = [];
      this.paymentDualFirm = false;
      this.paymentServiceRequestNumber = "";
      this.paymentCustomerName = "";
      this.paymentFirmName = "";
      this.paymentRajputInvoice = null;
      this.paymentRajdootInvoice = null;
    },
    isServiceRequestUuid(value) {
      return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        String(value || "")
      );
    },
    closeVoucherDropdown(index) {
      const row = this.voucherList[index];
      if (row && row.isDropdownOpen) {
        this.$set(this.voucherList[index], "isDropdownOpen", false);
      }
    },
    hasPaymentHistory(voucher) {
      return (
        voucher &&
        voucher.voucherType === "invoice" &&
        (parseFloat(voucher.received) || 0) > 0
      );
    },
    async handlePaymentHistory(voucher, index) {
      this.closeVoucherDropdown(index);
      const current = this.voucherList[index] || voucher;
      if (!current || !current.id) {
        this.$notify({
          message: "Cannot load payment history for this voucher",
          type: "warning",
          title: "Warning"
        });
        return;
      }

      this.paymentHistoryIdentifier = current.identifier || "";
      this.paymentHistoryFirm = current.firm || "";
      this.paymentHistoryTransactions = [];
      this.paymentHistoryTotal = 0;
      this.showPaymentHistoryPopup = true;
      this.loadingPaymentHistory = true;

      try {
        const params = new URLSearchParams();
        params.append("id", current.id);
        params.append("firm", current.firm || "");
        if (current.identifier) {
          params.append("identifier", current.identifier);
        }

        const response = await apiHelper.call(
          "get",
          `${endpoints.LARAVEL_BASE_URL}/api/v1/vouchers/payment-history?${params.toString()}`
        );

        const data = response.data || {};
        this.paymentHistoryTransactions = data.transactions || [];
        this.paymentHistoryTotal =
          parseFloat(data.totalReceived) ||
          this.paymentHistoryTransactions.reduce(
            (sum, tx) => sum + (parseFloat(tx.amount) || 0),
            0
          );
      } catch (error) {
        console.error("Error loading payment history:", error);
        this.$notify({
          message:
            (error.response &&
              error.response.data &&
              error.response.data.message) ||
            "Failed to load payment history",
          type: "danger",
          title: "Error"
        });
        this.showPaymentHistoryPopup = false;
      } finally {
        this.loadingPaymentHistory = false;
      }
    },
    isRajputInvoice(voucher) {
      return classifyFirm(voucher) === "rajput";
    },
    async handleViewSendInvoice(voucher, index) {
      this.closeVoucherDropdown(index);
      const current = this.voucherList[index] || voucher;
      if (!current || current.voucherType !== "invoice") {
        return;
      }

      try {
        if (this.isRajputInvoice(current)) {
          const saleId = current.id;
          if (!saleId) {
            throw new Error("Sale id missing for this invoice");
          }
          const status = await this.getReceiptDataForWhatsapp(saleId);
          if (status === 200) {
            this.showWhatsappSaleModal = true;
          }
          return;
        }

        // Vendor / Rajdoot invoice
        const serviceRequestId =
          current.serviceRequestId ||
          (this.isServiceRequestUuid(current.id) ? current.id : null);
        if (!serviceRequestId) {
          throw new Error("Service request id missing for vendor invoice");
        }

        const [serviceRequest, labors] = await Promise.all([
          this.fetchServiceRequest(serviceRequestId),
          this.getServiceRequestLabors(serviceRequestId)
        ]);

        const vendor = serviceRequest.vendor || {};
        const users = serviceRequest.users || serviceRequest.user || {};
        const vendorFirst = vendor.firstName || "";
        const vendorLast = vendor.lastName || "";
        const vendorFirmName = (
          vendor.firmName ||
          vendor.firm_name ||
          ""
        )
          .toString()
          .trim();
        // Match voucher list firm fallback:
        // firm_name OR "first_name last_name"
        const vendorDisplayName =
          vendorFirmName ||
          (current.firm || "").toString().trim() ||
          `${vendorFirst} ${vendorLast}`.trim() ||
          "Vendor";

        this.setVendorInvoiceData({
          labors: labors || [],
          externalGoodsMeta: serviceRequest.externalGoodsMeta || [],
          externalGoodsTotal: serviceRequest.externalGoodsTotal || 0,
          laborDiscount: serviceRequest.laborDiscount || 0,
          vendor: {
            name: vendorDisplayName,
            firmName: vendorFirmName,
            phone: vendor.phone || ""
          },
          customer: {
            name: `${users.firstName || ""} ${users.lastName || ""}`.trim() ||
              current.customerName ||
              "",
            phone: users.phone || ""
          },
          invoiceNumber:
            serviceRequest.vendorInvoice || current.identifier || "",
          invoiceDate: serviceRequest.invoiceDate || current.date || new Date(),
          serviceRequestId,
          serviceRequestNumber:
            serviceRequest.serviceRequestNumber ||
            current.serviceRequestNumber ||
            "",
          userId: serviceRequest.userId || users.userId || users.id,
          branchId: serviceRequest.branchId || null
        });
        this.showWhatsappVendorModal = true;
      } catch (error) {
        console.error("Error opening invoice:", error);
        this.$notify({
          message:
            (error && error.message) ||
            (error.response &&
              error.response.data &&
              error.response.data.message) ||
            "Could not open invoice",
          type: "danger",
          title: "Error"
        });
      }
    },
    onSaleInvoiceDialogOpen() {
      if (this.$refs && this.$refs.saleInvoice) {
        this.$refs.saleInvoice.fetchData();
      }
    },
    onSaleInvoiceDialogClose() {
      if (this.$refs && this.$refs.saleInvoice) {
        this.$refs.saleInvoice.resetProperty();
      }
    },
    onVendorInvoiceDialogOpen() {
      if (this.$refs && this.$refs.vendorInvoice) {
        this.$refs.vendorInvoice.fetchData();
      }
    },
    onVendorInvoiceDialogClose() {
      if (this.$refs && this.$refs.vendorInvoice) {
        this.$refs.vendorInvoice.resetProperty();
      }
    },
    async handleViewSendQuotation(voucher, index) {
      this.closeVoucherDropdown(index);
      const current = this.voucherList[index] || voucher;
      if (!current || current.voucherType !== "quotation" || !current.id) {
        this.$notify({
          message: "Cannot open quotation for this voucher",
          type: "warning",
          title: "Warning"
        });
        return;
      }

      try {
        const quotationData = await this.getUserQuotation(current.id);
        if (!quotationData) {
          throw new Error("Quotation not found");
        }
        this.quotationData = {
          ...quotationData,
          serviceRequestId: current.serviceRequestId || null,
          serviceRequestNumber: current.serviceRequestNumber || null,
        };
        this.showWhatsappQuotationModal = true;
      } catch (error) {
        console.error("Error opening quotation:", error);
        this.$notify({
          message:
            (error && error.message) ||
            (error.response &&
              error.response.data &&
              error.response.data.message) ||
            "Could not open quotation",
          type: "danger",
          title: "Error"
        });
      }
    },
    onQuotationDialogClose() {
      this.quotationData = null;
    },
    chalanDirectionLabel(voucher) {
      if (!this.isChalanVoucher(voucher)) return "";
      if (voucher.isInward === null || voucher.isInward === undefined) {
        return "";
      }
      return voucher.isInward ? "In" : "Out";
    },
    isChalanVoucher(voucher) {
      return (
        voucher &&
        (voucher.voucherType === "chalan" ||
          voucher.voucherType === "customer_chalan")
      );
    },
    chalanTypeLabel(source) {
      const data = source || {};
      if (data.type === "Inward" || data.type === "Outward") {
        return data.type;
      }
      const inward = data.is_inward != null ? data.is_inward : data.isInward;
      if (inward === true || inward === 1 || inward === "1") return "Inward";
      if (inward === false || inward === 0 || inward === "0") return "Outward";
      return "";
    },
    chalanNumberWithType(number, source) {
      const base = String(number || "").trim();
      if (!base) return "";
      const type = this.chalanTypeLabel(source);
      if (!type) return base;
      if (/\((Inward|Outward)\)\s*$/i.test(base)) return base;
      return `${base} (${type})`;
    },
    mapCustomerChalanToDesign(raw, voucher) {
      const data = raw || {};
      const nameParts = String(data.user || "")
        .trim()
        .split(/\s+/);
      const firstName = nameParts[0] || "";
      const lastName = nameParts.slice(1).join(" ") || "";
      const products = data.products || [];
      const chalanItems = products.map(item => {
        const price = parseFloat(item.price) || 0;
        const quantity = parseFloat(item.quantity) || 0;
        const taxRate = parseFloat(item.taxRate) || 0;
        const amount = parseFloat(item.total) || 0;
        const base = price * quantity;
        // Approximate tax portion of line total for display
        const taxAmount =
          taxRate > 0 ? amount - amount / (1 + taxRate / 100) : 0;
        return {
          name: item.name,
          price,
          quantity,
          taxRate,
          amount,
          taxAmount: Math.round(taxAmount * 100) / 100,
          // unused but keeps shape close to SR chalan items
          base
        };
      });

      const typeSource = Object.assign({}, data, {
        isInward:
          data.is_inward != null
            ? data.is_inward
            : data.isInward != null
            ? data.isInward
            : voucher && voucher.isInward
      });

      return {
        customerChalanId: data.id,
        chalanNumber: this.chalanNumberWithType(
          data.chalan_number || data.chalanNumber || "",
          typeSource
        ),
        updatedAt: data.date || data.updated_at || data.created_at || new Date(),
        users: {
          userId: data.userId,
          firstName,
          lastName,
          phone: data.phone || ""
        },
        chalanItems,
        total: parseFloat(data.sub_total) || 0,
        grandTotal: parseFloat(data.grand_total) || 0
      };
    },
    async handleViewSendChalan(voucher, index) {
      this.closeVoucherDropdown(index);
      const current = this.voucherList[index] || voucher;
      if (!current || !this.isChalanVoucher(current) || !current.id) {
        this.$notify({
          message: "Cannot open chalan for this voucher",
          type: "warning",
          title: "Warning"
        });
        return;
      }

      try {
        // Service-request chalan (has SR link) — same flow as Chalan List
        if (current.serviceRequestId) {
          const chalanData = await this.getServiceRequestChalanDetails(
            current.id
          );
          if (!chalanData) {
            throw new Error("Chalan not found");
          }
          const typeSource = Object.assign({}, chalanData, {
            isInward:
              chalanData.isInward != null
                ? chalanData.isInward
                : current.isInward
          });
          this.chalanData = Object.assign({}, chalanData, {
            chalanNumber: this.chalanNumberWithType(
              chalanData.chalanNumber,
              typeSource
            )
          });
          this.showWhatsappChalanModal = true;
          return;
        }

        // Counter / customer chalan
        const response = await apiHelper.call(
          "get",
          `${endpoints.LARAVEL_BASE_URL}/api/v1/customer-chalans/${current.id}`
        );
        const mapped = this.mapCustomerChalanToDesign(response.data, current);
        if (!mapped || !mapped.customerChalanId) {
          throw new Error("Chalan not found");
        }
        this.chalanData = mapped;
        this.showWhatsappChalanModal = true;
      } catch (error) {
        console.error("Error opening chalan:", error);
        this.$notify({
          message:
            (error && error.message) ||
            (error.response &&
              error.response.data &&
              error.response.data.message) ||
            "Could not open chalan",
          type: "danger",
          title: "Error"
        });
      }
    },
    onChalanDialogClose() {
      this.chalanData = null;
    },
    openPaymentPopup({
      invoices,
      dualFirm = false,
      serviceRequestNumber = "",
      rajputInvoice = null,
      rajdootInvoice = null,
      customerName = "",
      firmName = ""
    }) {
      const list = invoices || [];
      this.paymentInvoices = list;
      this.paymentDualFirm = !!dualFirm;
      this.paymentServiceRequestNumber = serviceRequestNumber || "";
      this.paymentRajputInvoice = rajputInvoice || null;
      this.paymentRajdootInvoice = rajdootInvoice || null;
      this.paymentCustomerName =
        customerName ||
        (list[0] && list[0].customerName) ||
        (rajputInvoice && rajputInvoice.customerName) ||
        (rajdootInvoice && rajdootInvoice.customerName) ||
        "";
      this.paymentFirmName = firmName || (list[0] && list[0].firm) || "";

      // Force a false→true transition so the popup watch always re-inits
      this.showPaymentPopup = false;
      this.$nextTick(() => {
        this.showPaymentPopup = true;
      });
    },
    async fetchServiceRequestInvoices(serviceRequestId, serviceRequestNumber = "") {
      const params = new URLSearchParams();
      // UUID is unique across years; number alone is not
      params.append("service_request_id", serviceRequestId);
      if (serviceRequestNumber) {
        // Helps older API builds that only know service_request_number
        params.append("service_request_number", serviceRequestNumber);
      }
      params.append("type", "invoice");
      params.append("per_page", "50");
      params.append("page", "1");

      const response = await apiHelper.call(
        "get",
        `${endpoints.LARAVEL_BASE_URL}/api/v1/vouchers?${params.toString()}`
      );

      const rows =
        (response.data && response.data.data) || response.data || [];
      const fromApi = rows
        .map(voucher => this.mapVoucherRow(voucher))
        .filter(
          // Client-side guard: never mix invoices from a different SR UUID
          inv => inv.serviceRequestId === serviceRequestId
        );

      // Fallback: any same-SR invoices already on the current voucher page
      // (covers paid labor bills still visible in the list)
      const fromPage = (this.voucherList || []).filter(
        inv =>
          inv &&
          inv.voucherType === "invoice" &&
          inv.serviceRequestId === serviceRequestId
      );

      const byKey = new Map();
      [...fromApi, ...fromPage].forEach(inv => {
        const key = inv.identifier || inv.id;
        if (key && !byKey.has(key)) byKey.set(key, inv);
      });
      return Array.from(byKey.values());
    },
    async handlePayment(voucher, index) {
      this.closeVoucherDropdown(index);

      // Use current list row (stable after dropdown close)
      const current = this.voucherList[index] || voucher;

      if (current.voucherType !== "invoice") {
        this.$notify({
          message: "Payment can only be made for invoices",
          type: "warning",
          title: "Warning"
        });
        return;
      }

      const balance = parseFloat(current.balance) || 0;

      // Service-request invoices: dual Rajput | Rajdoot view (UUID only)
      if (this.isServiceRequestUuid(current.serviceRequestId)) {
        this.selectedRows = [index];
        try {
          const related = await this.fetchServiceRequestInvoices(
            current.serviceRequestId,
            current.serviceRequestNumber
          );
          let sourceList =
            related && related.length > 0 ? related : [current];

          const clickedKey = current.id || current.identifier;
          if (
            clickedKey &&
            !sourceList.some(
              inv =>
                (inv.id && inv.id === current.id) ||
                (inv.identifier && inv.identifier === current.identifier)
            )
          ) {
            sourceList = [current, ...sourceList];
          }

          sourceList = sourceList.filter(
            inv =>
              !inv.serviceRequestId ||
              inv.serviceRequestId === current.serviceRequestId
          );

          const { rajputInvoice, rajdootInvoice } = splitServiceRequestInvoices(
            sourceList,
            current.firm
          );

          const payable = sourceList.filter(
            inv => inv.voucherType === "invoice" && (parseFloat(inv.balance) || 0) > 0
          );

          this.openPaymentPopup({
            invoices:
              payable.length > 0
                ? payable
                : balance > 0
                ? [current]
                : [],
            dualFirm: true,
            serviceRequestNumber: current.serviceRequestNumber,
            rajputInvoice,
            rajdootInvoice,
            customerName: current.customerName
          });
        } catch (error) {
          console.error("Error loading related invoices:", error);
          this.$notify({
            message:
              "Could not load related service-request invoices. Opening payment for this invoice only.",
            type: "warning",
            title: "Warning"
          });
          if (balance <= 0) {
            this.$notify({
              message: "This invoice has no outstanding balance",
              type: "warning",
              title: "Warning"
            });
            return;
          }
          this.openPaymentPopup({
            invoices: [current],
            dualFirm: false,
            firmName: current.firm,
            customerName: current.customerName
          });
        }
        return;
      }

      // Non–service-request invoice: simple single-invoice payment
      if (balance <= 0) {
        this.$notify({
          message: "This invoice has no outstanding balance",
          type: "warning",
          title: "Warning"
        });
        return;
      }

      this.selectedRows = [index];
      this.openPaymentPopup({
        invoices: [current],
        dualFirm: false,
        firmName: current.firm,
        customerName: current.customerName
      });
    },
    async handleMultiplePayment() {
      const nonInvoiceVouchers = this.selectedRows
        .map(index => this.voucherList[index])
        .filter(v => v.voucherType !== "invoice");

      if (nonInvoiceVouchers.length > 0) {
        this.$notify({
          message:
            "Please select only invoices for payment. Other voucher types cannot be paid together.",
          type: "danger",
          title: "Error"
        });
        return;
      }

      if (!this.validateSelectedInvoices()) {
        return;
      }

      const invoicesWithBalance = this.selectedInvoices.filter(
        inv => inv.balance > 0
      );

      if (invoicesWithBalance.length === 0) {
        this.$notify({
          message: "Selected invoices have no outstanding balance",
          type: "warning",
          title: "Warning"
        });
        return;
      }

      const srIds = [
        ...new Set(
          invoicesWithBalance
            .map(inv => inv.serviceRequestId)
            .filter(id => this.isServiceRequestUuid(id))
        )
      ];

      // Same service request (UUID) across firms → dual panel
      if (srIds.length === 1) {
        const srId = srIds[0];
        try {
          const related = await this.fetchServiceRequestInvoices(
            srId,
            invoicesWithBalance[0].serviceRequestNumber
          );
          let sourceList =
            related && related.length > 0 ? related : invoicesWithBalance;
          sourceList = sourceList.filter(
            inv => !inv.serviceRequestId || inv.serviceRequestId === srId
          );
          const { rajputInvoice, rajdootInvoice } = splitServiceRequestInvoices(
            sourceList,
            invoicesWithBalance[0].firm
          );
          const payable = sourceList.filter(
            inv => inv.voucherType === "invoice" && (inv.balance || 0) > 0
          );
          this.openPaymentPopup({
            invoices: payable.length > 0 ? payable : invoicesWithBalance,
            dualFirm: true,
            serviceRequestNumber: invoicesWithBalance[0].serviceRequestNumber,
            rajputInvoice,
            rajdootInvoice,
            customerName: invoicesWithBalance[0].customerName
          });
          return;
        } catch (error) {
          console.error("Error loading related invoices:", error);
        }
      }

      this.openPaymentPopup({
        invoices: invoicesWithBalance,
        dualFirm: false,
        firmName: invoicesWithBalance[0].firm,
        customerName: invoicesWithBalance[0].customerName
      });
    },
    validateSelectedInvoices() {
      if (this.selectedInvoices.length < 2) {
        return true;
      }

      const firstInvoice = this.selectedInvoices[0];
      const customerName = firstInvoice.customerName;
      const firm = firstInvoice.firm;
      const srId = firstInvoice.serviceRequestId;

      // Same service request UUID: allow cross-firm (Rajput + Rajdoot) selection
      const sameServiceRequest =
        this.isServiceRequestUuid(srId) &&
        this.selectedInvoices.every(
          inv =>
            inv.customerName === customerName &&
            inv.serviceRequestId === srId
        );

      if (sameServiceRequest) {
        return true;
      }

      const invalidInvoices = this.selectedInvoices.filter(
        inv => inv.customerName !== customerName || inv.firm !== firm
      );

      if (invalidInvoices.length > 0) {
        this.$notify({
          message:
            "All selected invoices must be from the same customer and same firm (or the same service request). Please adjust your selection.",
          type: "danger",
          title: "Error"
        });
        return false;
      }

      return true;
    },
    async handleSavePayment(paymentData) {
      this.savingPayment = true;
      try {
        const paymentPayload = {
          invoice_payments: paymentData.invoicePayments,
          payment_breakdown: paymentData.paymentBreakdown,
          payment_note: paymentData.paymentNote,
          total_paid: paymentData.totalPaid
        };

        await apiHelper.call(
          "post",
          `${endpoints.LARAVEL_BASE_URL}/api/v1/vouchers/payment`,
          paymentPayload
        );

        this.$notify({
          message: "Payment saved successfully!",
          type: "success",
          title: "Success"
        });
        this.showPaymentPopup = false;
        this.resetPaymentContext();

        await this.fetchVouchers();

        this.selectedRows = [];
        this.selectAll = false;
      } catch (error) {
        console.error("Error saving payment:", error);
        const errorMessage =
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          "Failed to save payment";
        this.$notify({
          message: errorMessage,
          type: "danger",
          title: "Error"
        });
      } finally {
        this.savingPayment = false;
      }
    }
  }
};
</script>
